import {
  Box,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import {
  DeleteOutlined,
  Download,
  InsertDriveFileOutlined,
  PictureAsPdf,
  VisibilityOutlined,
} from "@mui/icons-material";
import { useDispatchWrapper } from "../../../hooks";
import { showMessage } from "../../../redux/messages/messagesActions";
import { downLoadFile, getFileNameFromUrl, previewFile } from "../utils";
import { withS3Prefix } from "../../../helpers";
import { IFileUpload } from "../FileUpload.type";
import produce from "immer";
import { LightBox } from "../../LightBox";

const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|svg)$/i;
const PDF_EXT_RE = /\.pdf$/i;

/** Resolve the display info for a single attachment row. */
const resolveFileMeta = (item: IFileUpload) => {
  const fileName =
    item.file?.name || item.name || getFileNameFromUrl(item.path || "");
  const rawPath = item.path || "";
  const isImage = IMAGE_EXT_RE.test(fileName || rawPath);
  const isPdf = PDF_EXT_RE.test(fileName || rawPath);
  const imageUrl = item.file
    ? URL.createObjectURL(item.file)
    : rawPath
    ? withS3Prefix(rawPath)
    : "";
  return { fileName, isImage, isPdf, imageUrl };
};

export const MultiFileDisplay: React.FC<{
  value: IFileUpload[];
  onChange: (files: IFileUpload[]) => void;
  deleteDisabled?: boolean;
  variant?: "GALLERY" | "NORMAL";
  galleryColumns?: number;
  galleryTileSize?: number;
}> = (props) => {
  const {
    value,
    onChange,
    deleteDisabled,
    variant = "NORMAL",
    galleryColumns = 3,
    galleryTileSize,
  } = props;
  const dispatch = useDispatchWrapper();
  const [loading, setLoading] = React.useState<number | null>(null);

  /* LightBox state for the GALLERY variant. Holds the resolved image URLs and
     the index that should open first. */
  const [lightbox, setLightbox] = React.useState<{
    open: boolean;
    images: string[];
  }>({ open: false, images: [] });

  const openGallery = () => {
    const images = value
      .map((item) => resolveFileMeta(item))
      .filter((m) => m.isImage && m.imageUrl)
      .map((m) => m.imageUrl);
    if (images.length > 0) setLightbox({ open: true, images });
  };

  const handleDownloadFile = (row: IFileUpload, index: number) => async () => {
    if (row.file) {
      const downloadLink = URL.createObjectURL(row.file);
      const a = document.createElement("a");
      a.href = downloadLink;
      a.download = row.file.name || "download";
      a.click();
      URL.revokeObjectURL(downloadLink);
      return;
    }

    if (!loading && row.path) {
      try {
        setLoading(index);
        await downLoadFile(row.path);
      } catch (err) {
        dispatch(
          showMessage({
            displayAs: "snackbar",
            message: "Someting went to be wrong!",
            type: "error",
          })
        );
      } finally {
        setLoading(null);
      }
    }
  };

  const handlePreviewFile = (row: IFileUpload, index: number) => async () => {
    if (row.file) {
      const downloadLink = URL.createObjectURL(row.file);
      window.open(downloadLink, "_target");
      return;
    }

    if (!loading && row.path) {
      try {
        setLoading(index);
        await previewFile(row.path);
      } catch (err) {
        dispatch(
          showMessage({
            displayAs: "snackbar",
            message: "Someting went to be wrong!",
            type: "error",
          })
        );
      } finally {
        setLoading(null);
      }
    }
  };

  const handleDelete = (rowIndex: number) => () => {
    if (!deleteDisabled) {
      const newValue = produce(value, (draftValues) => {
        draftValues.splice(rowIndex, 1);
      });
      onChange(newValue);
    }
  };

  // ─── GALLERY variant: responsive thumbnail grid ──────────────────────────
  if (variant === "GALLERY") {
    /* Small fixed-size tiles get tighter action buttons so all three fit. */
    const isCompact = !!galleryTileSize && galleryTileSize < 140;
    const actionButtonSx = isCompact
      ? { p: 0.4, "& .MuiSvgIcon-root": { fontSize: 16 } }
      : {};
    return (
      <>
        <Box
          mt={2}
          sx={{
            display: "grid",
            gridTemplateColumns: galleryTileSize
              ? `repeat(auto-fill, ${galleryTileSize}px)`
              : `repeat(${Math.max(1, galleryColumns)}, 1fr)`,
            gap: galleryTileSize ? 1 : 1.5,
          }}
        >
          {value.map((item, index) => {
            const { fileName, isImage, isPdf, imageUrl } =
              resolveFileMeta(item);
            const isClickable = isImage || isPdf;
            return (
              <Box
                key={index}
                sx={{
                  position: "relative",
                  aspectRatio: "1 / 1",
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                  boxShadow: isCompact ? 0 : 1,
                  cursor: isClickable ? "pointer" : "default",
                  "&:hover": {
                    boxShadow: isCompact ? 2 : 4,
                    "& .gallery-actions": { opacity: 1 },
                  },
                  transition: "all 0.2s ease-in-out",
                }}
                onClick={
                  isImage
                    ? openGallery
                    : isPdf
                    ? handlePreviewFile(item, index)
                    : undefined
                }
              >
                {isImage && imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={fileName}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                ) : isPdf ? (
                  /* PDF — full-width PDF icon filling the tile */
                  <PictureAsPdf
                    titleAccess={fileName}
                    sx={{
                      width: "100%",
                      height: "100%",
                      color: "error.main",
                      p: isCompact ? 2.5 : 2,
                    }}
                  />
                ) : (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    spacing={0.5}
                    sx={{ width: "100%", height: "100%", p: isCompact ? 0.5 : 1 }}
                  >
                    <InsertDriveFileOutlined
                      fontSize={isCompact ? "medium" : "large"}
                      sx={{ color: "primary.main" }}
                    />
                    <Typography
                      variant="caption"
                      noWrap
                      title={fileName}
                      sx={{ maxWidth: "100%" }}
                    >
                      {fileName}
                    </Typography>
                  </Stack>
                )}

                {loading === index && (
                  <LinearProgress
                    color="primary"
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: 2,
                    }}
                  />
                )}

                {/* Hover overlay — a full-tile scrim that fades in on hover
                    with the action icons centered on it. Cleaner and more
                    legible than corner chips over any image/PDF. */}
                <Box
                  className="gallery-actions"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: isCompact ? 0.25 : 0.5,
                    opacity: 0,
                    transition: "opacity 0.2s ease-in-out",
                    background:
                      "linear-gradient(to bottom, rgba(0,0,0,.15), rgba(0,0,0,.55))",
                    backdropFilter: "blur(1px)",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Tooltip title="Preview">
                    <span>
                      <IconButton
                        size="small"
                        sx={{
                          color: "info.main",
                          bgcolor: "#fff",
                          boxShadow: 2,
                          "&:hover": { bgcolor: "info.main", color: "#fff" },
                          ...actionButtonSx,
                        }}
                        onClick={
                          isImage ? openGallery : handlePreviewFile(item, index)
                        }
                        disabled={loading === index}
                      >
                        <VisibilityOutlined fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Download">
                    <span>
                      <IconButton
                        size="small"
                        sx={{
                          color: "success.main",
                          bgcolor: "#fff",
                          boxShadow: 2,
                          "&:hover": { bgcolor: "success.main", color: "#fff" },
                          ...actionButtonSx,
                        }}
                        onClick={handleDownloadFile(item, index)}
                        disabled={loading === index}
                      >
                        <Download fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <span>
                      <IconButton
                        size="small"
                        sx={{
                          color: "error.main",
                          bgcolor: "#fff",
                          boxShadow: 2,
                          "&:hover": { bgcolor: "error.main", color: "#fff" },
                          ...actionButtonSx,
                        }}
                        onClick={handleDelete(index)}
                        disabled={deleteDisabled || loading === index}
                      >
                        <DeleteOutlined fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>

        <LightBox
          open={lightbox.open}
          images={lightbox.images}
          variant="GALLERY"
          onClose={() => setLightbox({ open: false, images: [] })}
        />
      </>
    );
  }

  // ─── NORMAL variant: vertical list of file rows ──────────────────────────
  return (
    <Stack spacing={1.5} mt={2}>
      {value.map((item, index) => {
        const { fileName, isImage, imageUrl } = resolveFileMeta(item);

        return (
          <Stack
            key={index}
            direction="row"
            alignItems="center"
            borderRadius={2}
            position="relative"
            p={2}
            spacing={2}
            sx={{
              backgroundColor: "background.paper",
              boxShadow: 1,
              border: "1px solid",
              borderColor: "divider",
              "&:hover": {
                boxShadow: 4,
                "& .file-actions": { opacity: 1 },
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            {/* File Type Icon / Image Thumbnail */}
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 1,
                backgroundColor: "primary.light",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                overflow: "hidden",
                cursor: isImage ? "pointer" : "default",
              }}
              onClick={isImage ? handlePreviewFile(item, index) : undefined}
            >
              {isImage && imageUrl ? (
                <img
                  src={imageUrl}
                  alt={fileName}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <InsertDriveFileOutlined
                  fontSize="medium"
                  sx={{ color: "primary.main" }}
                />
              )}
            </Box>

            {/* File Name */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                noWrap
                sx={{ fontWeight: 600 }}
                title={fileName}
              >
                {fileName}
              </Typography>
              {/* Optional: metadata like file size or uploaded date */}
              {/* <Typography variant="body2" color="text.secondary">
                Uploaded 2 days ago
              </Typography> */}
            </Box>

            {/* Loading Indicator */}
            {loading === index && (
              <LinearProgress
                color="primary"
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 2,
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                }}
              />
            )}

            {/* Action Buttons */}
            <Box
              className="file-actions"
              sx={{
                display: "flex",
                gap: 1,
                opacity: 0,
                transition: "opacity 0.2s",
                ml: 1,
              }}
            >
              <Tooltip title="Preview">
                <span>
                  <IconButton
                    size="small"
                    onClick={handlePreviewFile(item, index)}
                    disabled={loading === index}
                  >
                    <VisibilityOutlined fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Downloadddd">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleDownloadFile(item, index)}
                    disabled={loading === index}
                  >
                    <Download fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Delete">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleDelete(index)}
                    disabled={deleteDisabled || loading === index}
                  >
                    <DeleteOutlined fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
};
